/**
 * Auth feature-local constants (ADR-0009): error codes the UI branches on and the
 * client-facing policy numbers. Cross-cutting values (route paths) live in
 * `@/config/routes`; these are auth-specific and exported via the feature barrel.
 */

/** Stable error codes returned by the tracking auth realm (ADR-0008). Branch on these, never raw strings. */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_TEMPORARILY_LOCKED: 'ACCOUNT_TEMPORARILY_LOCKED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INVALID_OTP: 'INVALID_OTP',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  RESEND_COOLDOWN_ACTIVE: 'RESEND_COOLDOWN_ACTIVE',
  INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  /** QA-observed (US-112329): the deployed tracking backend sends this instead of the
   *  agreed `CUSTOMER_NOT_FOUND` (api.json) for `/auth/tracking/login` and
   *  `/auth/tracking/login/otp/send`. Treated as an alias everywhere CUSTOMER_NOT_FOUND
   *  is checked so the inline "not registered" field error still fires instead of falling
   *  through to the generic toast. */
  PHONE_NOT_REGISTERED: 'PHONE_NOT_REGISTERED',
  /** US-112329: FE-proposed, pending BE confirmation — not yet modeled in api.json's
   *  Tracking login examples. Name/status code chosen to match the ACCOUNT_DEACTIVATED
   *  precedent used elsewhere in the same Postman collection (story 110089). */
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  RESET_TOKEN_EXPIRED: 'RESET_TOKEN_EXPIRED',
  PASSWORD_REUSED: 'PASSWORD_REUSED',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/** Client-facing auth policy. Server is the source of truth; these drive UI defaults/timers. */
export const AUTH_POLICY = {
  /** Default OTP resend cooldown when the server doesn't supply one. */
  resendCooldownSeconds: 60,
  /** Max OTP resends per challenge. */
  maxResends: 5,
} as const;

/**
 * Copy for the green success toast every auth POST now raises (`<MutationToaster>`).
 *
 * Sign-out is deliberately absent: it lands the grower on the login screen, which announces itself,
 * and a *forced* sign-out is already narrated by `<AppShell>`'s force-logout toast.
 */
export const AUTH_SUCCESS_COPY = {
  SIGNED_IN: 'Signed in successfully.',
  CODE_SENT: 'Verification code sent.',
  CODE_RESENT: 'OTP has been resent successfully',
  CODE_VERIFIED: 'Code verified.',
  PASSWORD_RESET: 'Password reset successfully. Please log in.',
} as const;

/** US-117535 (grilling Q6): the AC's own logout copy, both ways — not the server's message. */
export const LOGOUT_COPY = {
  SUCCESS: "You've been logged out.",
  FAILED: "We couldn't log you out. Please try again.",
} as const;

/** The two account types this app's auth realm confirms (ADR-0029). A third type is a backend
 *  change this union must grow with — see `otherAccountType`, the one place the binary pairing
 *  is load-bearing rather than just a label lookup. */
export type AccountType = 'grower' | 'associate_grower';

function isAccountType(value: string): value is AccountType {
  return value === 'grower' || value === 'associate_grower';
}

/**
 * ADR-0029 — confirmed live (DevTools capture, 2026-10-14): `POST .../login/otp/verify` answers
 * `availableAccounts: ["grower", "associate_grower"]` — plain account-type codes, not
 * `{accountType, label}` objects. The server sends no label, so the client renders one from this
 * map, the same way greenway_mobile's `AccountType.label` getter does.
 */
export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  grower: 'Grower',
  associate_grower: 'Associate Grower',
};

export function accountTypeLabel(accountType: string): string {
  return isAccountType(accountType) ? ACCOUNT_TYPE_LABEL[accountType] : accountType;
}

/** The binary pairing `otherAccountType` resolves — explicit so a third `AccountType` member is a
 *  type error here rather than a silently wrong lookup. */
const OTHER_ACCOUNT_TYPE: Record<AccountType, AccountType> = {
  grower: 'associate_grower',
  associate_grower: 'grower',
};

/**
 * ADR-0029 correction (2026-10-20): `GET /profile/tracking` sends `canSwitchAccount` and
 * `activeAccount` but never the guessed `otherAccountLabel` — confirmed live, a dual-role account
 * (`canSwitchAccount: true`) got no "Switch Profile" row because the row's visibility was keyed on
 * a field the server never sends. The pairing is binary (the confirmed account types are exactly
 * `ACCOUNT_TYPE_LABEL`'s two keys), so "the other" account is whichever known type isn't the
 * active one — no new field or login-time storage needed. Mirrors greenway_mobile's
 * `AuthenticatedUser.otherAccount` getter, which does this same lookup against its persisted
 * `availableAccounts` and labels the result via `AccountType.label`'s static switch — mobile never
 * trusts a server-sent label string either.
 *
 * Returns `null` for an `activeAccount` this union doesn't know (e.g. a Dealer session) rather
 * than guessing one of the two known types.
 */
export function otherAccountType(activeAccount: string | null): AccountType | null {
  if (!activeAccount || !isAccountType(activeAccount)) return null;
  return OTHER_ACCOUNT_TYPE[activeAccount];
}

/** Figma node 2356-1002627 — per-role blurb on the Select Account card. Static copy, not server-sent. */
export const ACCOUNT_TYPE_DESCRIPTION: Record<AccountType, string> = {
  grower: 'Manage your farm, devices, subscriptions, and Associate Growers.',
  associate_grower: 'Manage assigned devices and control irrigation in the field.',
};

export function accountTypeDescription(accountType: string): string | undefined {
  return isAccountType(accountType) ? ACCOUNT_TYPE_DESCRIPTION[accountType] : undefined;
}
